import React, { useEffect, useState } from 'react'
import { ColDef } from '@ag-grid-community/core';

import DataTable from '../../reusables/DataTable';

import { useGetSubmittedImmigrationApplications, useApproveOrResetApplication } from '../../../hooks/immigration_applications';

import { ISubmittedImmigrationApplicationsForConsultant } from '../../../types/immigration_application.mix';

interface IImmigrationApplicationsProps {
    applicantId: number;
};

interface IAccordionData {
    applicationId: number;
    questTrackingId: number | null;
    documents: boolean | null;
    forms: boolean | null;
};

export const ImmigrationApplications = ({ applicantId }: IImmigrationApplicationsProps) => {

    const [accordionsData, setAccordionsData] = useState<IAccordionData[]>([]);
    const [applications, setApplications] = useState<ISubmittedImmigrationApplicationsForConsultant[]>([]);

    const { data: applicationsData, isLoading, isError } = useGetSubmittedImmigrationApplications(applicantId);
    const { mutate: approveOrResetApplication } = useApproveOrResetApplication();

    useEffect(() => {
        if (applicationsData) {
            setApplications(applicationsData);

            const oAccordionsData: IAccordionData[] = [];

            applicationsData.forEach(application => {
                application.ia_quest_trackings.forEach(questTracking => {
                    oAccordionsData.push({
                        applicationId: application.id,
                        questTrackingId: questTracking.id,
                        documents: null,
                        forms: null
                    });
                });

                oAccordionsData.push({
                    applicationId: application.id,
                    questTrackingId: null,
                    documents: true,
                    forms: null
                });

                oAccordionsData.push({
                    applicationId: application.id,
                    questTrackingId: null,
                    documents: null,
                    forms: true
                });
            });

            setAccordionsData(oAccordionsData);
        };
    }, [applicationsData]);

    const questionnaireColumns: ColDef[] = [
        { width: 800, field: "question", headerName: "Question", sortable: false },
        { width: 400, field: "answer", headerName: "Answer", sortable: false, filter: false },
    ];

    const documentsColumns: ColDef[] = [
        {
            width: 300,
            field: "documentGroup",
            headerName: "Document Group",
            sort: 'asc',
            valueGetter: ({ data }) => data?.ia_document?.ia_document_group?.title
        },
        {
            width: 600,
            field: "name",
            headerName: "Name",
            sort: 'asc',
            sortable: false,
            valueGetter: ({ data }) => data?.ia_document?.title
        },
        {
            width: 200,
            field: "url",
            headerName: "Action",
            sortable: false,
            filter: false,
            valueFormatter: (params) => params.value ? "Yes" : "No",
            cellRenderer: (params: { value: string | null }) => params.value ? <a style={{ height: "100%" }} href={params.value} target="_blank" rel="noopener noreferrer">View Document</a> : "N/A"
        },
    ];

    const formsColumns: ColDef[] = [
        { width: 200, field: "title", headerName: "Title" },
        {
            width: 200,
            field: "original_form_url",
            headerName: "Original Form URL",
            sortable: false,
            filter: false,
            valueFormatter: (params) => params.value ? "Yes" : "No",
            cellRenderer: (params: { value: string | null }) => params.value ? <a style={{ height: "100%" }} href={params.value} target="_blank" rel="noopener noreferrer">View Form</a> : "N/A"
        },
        {
            width: 200,
            field: "filled_form_url",
            headerName: "Completed Form URL",
            sortable: false,
            filter: false,
            valueFormatter: (params) => params.value ? "Yes" : "No",
            cellRenderer: (params: { value: string | null }) => params.value ? <a style={{ height: "100%" }} href={params.value} target="_blank" rel="noopener noreferrer">View Form</a> : "N/A"
        },
    ];

    const getIsAccordionActive = (applicationId: number, questTrackingId: number | null, documents: boolean | null, forms: boolean | null) => {
        return !!accordionsData.find(accordion => accordion.applicationId === applicationId && accordion.questTrackingId === questTrackingId && accordion.documents === documents && accordion.forms === forms);
    };

    const toggleAccordion = (type: "questionnaire" | "documents" | "forms", applicationId: number, questTrackingId: number | null) => {
        const isActive = getIsAccordionActive(applicationId, questTrackingId, type === "documents" ? true : null, type === "forms" ? true : null);
        if (isActive) {
            setAccordionsData(accordionsData.filter(accordion => accordion.applicationId !== applicationId || accordion.questTrackingId !== questTrackingId || accordion.documents !== (type === "documents" ? true : null) || accordion.forms !== (type === "forms" ? true : null)));
        } else {
            if (type === "questionnaire") {
                setAccordionsData([...accordionsData, { applicationId: applicationId, questTrackingId: questTrackingId, documents: null, forms: null }]);
            } else if (type === "documents") {
                setAccordionsData([...accordionsData, { applicationId: applicationId, questTrackingId: null, documents: true, forms: null }]);
            } else if (type === "forms") {
                setAccordionsData([...accordionsData, { applicationId: applicationId, questTrackingId: null, documents: null, forms: true }]);
            }
        }
    };

    const onApproveOrResetApplication = (tracking: ISubmittedImmigrationApplicationsForConsultant) => {
        approveOrResetApplication({ applicantId, iaTrackingId: tracking.id });
    };

    if (isLoading) return (
        <div className="imm_consultant__loading">
            <div className="imm_consultant__process_loader" />
            Loading...
        </div>
    );
    if (!isLoading && isError) return (
        <div className="imm_consultant__loading">
            <div className="imm_consultant__process_loader" />
            Something went wrong. Please try again later.
        </div>
    );

    if (!isLoading && !applications.length) return (
        <div className="imm_consultant__loading">
            No applications found.
        </div>
    );

    return (
        <div className='imm_consultant__applicant_details__immigration_applications'>
            {applications.map((application) => (
                <div key={application.id} className='imm_consultant__applicant_details__immigration_applications__application'>
                    <div className="imm_consultant__applicant_details__immigration_applications__application__header">
                        <div className="imm_consultant__applicant_details__immigration_applications__application__header__title">
                            <h4>{application.immigration_application.title}</h4>
                            {
                                !application.approved_at
                                    ? <div className='imm_consultant__applicant_details__immigration_applications__application__header__title__approval_pill__pending'>Pending approval</div>
                                    : <div className='imm_consultant__applicant_details__immigration_applications__application__header__title__approval_pill__approved'>Approved</div>
                            }
                        </div>
                        <div className="imm_consultant__applicant_details__immigration_applications__application__header__actions">
                            <button
                                className={`imm_consultant__btn btn_fit_content ${application.approved_at ? "imm_consultant__btn__secondary" : "imm_consultant__btn__primary"}`}
                                onClick={() => onApproveOrResetApplication(application)}
                            >
                                {application.approved_at ? "Remove Approval" : "Approve Application"}
                            </button>
                        </div>
                    </div>
                    {application.ia_quest_trackings.map((questTracking) => (
                        <div key={questTracking.id} className={`imm_consultant__applicant_details__immigration_applications__accordion ${getIsAccordionActive(application.id, questTracking.id, null, null) ? "imm_consultant__applicant_details__immigration_applications__accordion__active" : ""}`}>
                            <div
                                className="imm_consultant__applicant_details__immigration_applications__accordion__header"
                                onClick={() => toggleAccordion("questionnaire", application.id, questTracking.id)}
                            >
                                {questTracking.instance.questionnaire.name}
                            </div>
                            <div className="imm_consultant__applicant_details__immigration_applications__accordion__content">
                                <div className="imm_consultant__applicant_details__immigration_applications__accordion__content__data_table">
                                    <DataTable data={questTracking.instance.answers_list} columns={questionnaireColumns} paginationPageSize={200} />
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className={`imm_consultant__applicant_details__immigration_applications__accordion ${getIsAccordionActive(application.id, null, true, null) ? "imm_consultant__applicant_details__immigration_applications__accordion__active" : ""}`}>
                        <div
                            className="imm_consultant__applicant_details__immigration_applications__accordion__header"
                            onClick={() => toggleAccordion("documents", application.id, null)}
                        >
                            Documents
                        </div>
                        <div className="imm_consultant__applicant_details__immigration_applications__accordion__content">
                            <div className="imm_consultant__applicant_details__immigration_applications__accordion__content__data_table">
                                <DataTable data={application.ia_eligible_docs} columns={documentsColumns} paginationPageSize={200} />
                            </div>
                        </div>
                    </div>

                    <div className={`imm_consultant__applicant_details__immigration_applications__accordion ${getIsAccordionActive(application.id, null, null, true) ? "imm_consultant__applicant_details__immigration_applications__accordion__active" : ""}`}>
                        <div
                            className="imm_consultant__applicant_details__immigration_applications__accordion__header"
                            onClick={() => toggleAccordion("forms", application.id, null)}
                        >
                            Forms
                        </div>
                        <div className="imm_consultant__applicant_details__immigration_applications__accordion__content">
                            <div className="imm_consultant__applicant_details__immigration_applications__accordion__content__data_table">
                                <DataTable data={application.ia_form_trackings.map(formTracking => ({
                                    ...formTracking,
                                    title: formTracking.ia_form.title,
                                    original_form_url: formTracking.ia_form.form_url,
                                    prefilled_form_url: formTracking.prefilled_form_url,
                                    filled_form_url: formTracking.filled_form_url
                                }))} columns={formsColumns} paginationPageSize={200} />
                            </div>
                        </div>
                    </div>
                    <br /><br />
                </div>
            ))}
        </div>
    );
};